import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  listDidGroups,
  getDidGroup,
  createDidGroup,
  updateDidGroup,
  updateDidGroupForwarding,
  clearDidGroupBlocks,
  deleteDidGroup,
  releaseDidGroup,
  removeQuarantinedFromGroup,
  getReleaseJob,
  addGroupMembers,
  removeGroupMember,
  removeGroupMembersBulk,
  bulkMoveDids,
  bulkDidAction,
  bulkCreateDids,
  provisionDids,
  updateDid,
  listDids,
  type DidGroup,
  type Did,
  type ReleaseJob,
} from "../api/dids";
import { listEligibleCampaigns } from "../api/queues";
import { ListOveragePlannerPanel } from "../components/ListOveragePlannerPanel";

function statusBadge(d: Did) {
  if (d.is_retired)
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">retired</span>;
  if (d.is_blocked || d.is_quarantined)
    return (
      <span
        className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800"
        title={d.block_reason ?? ""}
      >
        {d.block_reason ? `blocked: ${d.block_reason}` : "quarantined"}
      </span>
    );
  return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">active</span>;
}

function formatForwarding(
  actionType?: string | null,
  actionData?: string | null,
  inboundMode?: string | null,
) {
  if (!actionType || !actionData) return "Default / Unset";
  const modeTag = inboundMode ? ` · ${inboundMode}` : "";
  if (actionType === "external") return `Forward → ${actionData}${modeTag}`;
  if (actionType === "agents") return `Agents (Campaign #${actionData})${modeTag}`;
  if (actionType === "sip") return `SIP → ${actionData}${modeTag}`;
  return `${actionType}: ${actionData}${modeTag}`;
}

function AddMembersModal({
  groupId,
  groupName,
  allGroups,
  onClose,
  onSaved,
}: {
  groupId: number;
  groupName: string;
  allGroups: DidGroup[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [sourceMode, setSourceMode] = useState<"unassigned" | "from_pool" | "all">("unassigned");
  const [sourcePoolId, setSourcePoolId] = useState<number | "">(
    allGroups.find((g) => g.did_group_id !== groupId)?.did_group_id ?? "",
  );
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const queryParams = useMemo(() => {
    if (sourceMode === "unassigned") {
      return { unassigned: true, per_page: 500, q: search.trim() || undefined };
    }
    if (sourceMode === "from_pool" && sourcePoolId !== "") {
      return { did_group_id: Number(sourcePoolId), per_page: 500, q: search.trim() || undefined };
    }
    return { per_page: 500, q: search.trim() || undefined };
  }, [sourceMode, sourcePoolId, search]);

  const { data, isLoading } = useQuery({
    queryKey: ["dids", "add-members-modal", groupId, queryParams],
    queryFn: () => listDids(queryParams),
    enabled: sourceMode !== "from_pool" || sourcePoolId !== "",
  });

  const save = useMutation({
    mutationFn: () => addGroupMembers(groupId, [...selected]),
    onSuccess: onSaved,
  });

  // Exclude numbers already in the target group
  const dids = useMemo(
    () => (data?.data ?? []).filter((d) => d.did_group_id !== groupId),
    [data, groupId],
  );

  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const allVisibleSelected = dids.length > 0 && dids.every((d) => selected.has(d.did_id));
  const toggleAllVisible = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected) {
        dids.forEach((d) => n.delete(d.did_id));
      } else {
        dids.forEach((d) => n.add(d.did_id));
      }
      return n;
    });
  };

  const otherGroups = allGroups.filter((g) => g.did_group_id !== groupId);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
      data-testid="add-members-modal"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-full overflow-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Add or Move Numbers into "{groupName}"</h2>
            <p className="text-xs text-gray-500">
              Select unassigned numbers or move numbers directly from another pool.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">
            ✕
          </button>
        </div>

        {/* Source tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setSourceMode("unassigned")}
            data-testid="source-tab-unassigned"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              sourceMode === "unassigned"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Unassigned Numbers
          </button>
          <button
            type="button"
            onClick={() => setSourceMode("from_pool")}
            data-testid="source-tab-from-pool"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              sourceMode === "from_pool"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Move from Another Pool
          </button>
          <button
            type="button"
            onClick={() => setSourceMode("all")}
            data-testid="source-tab-all"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              sourceMode === "all"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All My Numbers
          </button>

          {sourceMode === "from_pool" && (
            <select
              value={sourcePoolId}
              onChange={(e) => setSourcePoolId(e.target.value ? Number(e.target.value) : "")}
              data-testid="source-pool-select"
              className="border border-gray-300 rounded px-2.5 py-1 text-xs ml-auto"
            >
              <option value="">Select source pool…</option>
              {otherGroups.map((g) => (
                <option key={g.did_group_id} value={g.did_group_id}>
                  {g.name} ({g.member_count})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Search & Select All */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search by phone number, area code, or label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="add-members-search"
            className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
          {dids.length > 0 && (
            <button
              type="button"
              onClick={toggleAllVisible}
              data-testid="add-members-select-all"
              className="px-3 py-1.5 rounded border border-gray-300 text-xs hover:bg-gray-50 whitespace-nowrap"
            >
              {allVisibleSelected ? "Deselect Visible" : `Select All (${dids.length})`}
            </button>
          )}
        </div>

        {isLoading ? (
          <p className="text-gray-500 text-sm py-4">Loading numbers…</p>
        ) : dids.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">
            {sourceMode === "unassigned"
              ? "No unassigned numbers match. Switch to 'Move from Another Pool' or 'All My Numbers' to move numbers from existing pools."
              : "No matching numbers found in the selected source."}
          </p>
        ) : (
          <div className="border border-gray-200 rounded max-h-80 overflow-auto divide-y divide-gray-100">
            {dids.map((d) => (
              <label
                key={d.did_id}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={selected.has(d.did_id)}
                    onChange={() => toggle(d.did_id)}
                    data-testid={`add-member-checkbox-${d.phone}`}
                  />
                  <span className="font-mono font-medium">{d.phone}</span>
                  <span className="text-gray-500 text-xs">{d.name || ""}</span>
                </div>
                <div className="flex items-center gap-2">
                  {d.did_group_name ? (
                    <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                      From: {d.did_group_name}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                      Unassigned
                    </span>
                  )}
                  {statusBadge(d)}
                </div>
              </label>
            ))}
          </div>
        )}

        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-gray-500">
            {selected.size} number{selected.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
            >
              Cancel
            </button>
            <button
              disabled={selected.size === 0 || save.isPending}
              onClick={() => save.mutate()}
              data-testid="confirm-add-members-btn"
              className="px-5 py-2 bg-brand hover:bg-brand-dark text-white rounded text-sm font-medium disabled:opacity-50"
            >
              {save.isPending
                ? "Moving…"
                : `Move ${selected.size || ""} to "${groupName}"`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AcquireIntoPoolModal({
  groupId,
  groupName,
  initialTab = "import",
  onClose,
  onSaved,
}: {
  groupId: number;
  groupName: string;
  initialTab?: "import" | "carrier" | "list_overage";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tab, setTab] = useState<"import" | "carrier" | "list_overage">(initialTab);
  const [phonesText, setPhonesText] = useState("");
  const [namePrefix, setNamePrefix] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [quantity, setQuantity] = useState(5);
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  const importMut = useMutation({
    mutationFn: () => {
      const phones = phonesText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      return bulkCreateDids({
        phones,
        name_prefix: namePrefix.trim() || undefined,
        did_group_id: groupId,
      });
    },
    onSuccess: (res) => {
      setResultMsg(
        `Added ${res.created} number(s) to "${groupName}"` +
          (res.skipped_duplicates.length
            ? ` (${res.skipped_duplicates.length} duplicate skipped)`
            : ""),
      );
      onSaved();
    },
  });

  const orderMut = useMutation({
    mutationFn: () =>
      provisionDids({
        area_code: areaCode.trim(),
        quantity: Number(quantity) || 1,
        did_group_id: groupId,
      }),
    onSuccess: (res) => {
      setResultMsg(
        `Ordered ${res.count} number(s) in area code ${areaCode} ($${res.charged}) into "${groupName}".`,
      );
      onSaved();
    },
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4 overflow-y-auto"
      data-testid="acquire-into-pool-modal"
    >
      <div
        className={`bg-white rounded-lg shadow-xl p-6 w-full ${
          tab === "list_overage" ? "max-w-2xl" : "max-w-lg"
        } space-y-4 my-auto`}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Add / Order Numbers into "{groupName}"</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">
            ✕
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          <button
            type="button"
            onClick={() => {
              setTab("import");
              setResultMsg(null);
            }}
            data-testid="acquire-tab-import"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "import"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Register / Bulk Paste Numbers
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("carrier");
              setResultMsg(null);
            }}
            data-testid="acquire-tab-carrier"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "carrier"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Order from Carrier by Area Code
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("list_overage");
              setResultMsg(null);
            }}
            data-testid="acquire-tab-list-overage"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "list_overage"
                ? "bg-indigo-600 text-white"
                : "bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
            }`}
          >
            Buy Overage by Contact List
          </button>
        </div>

        {tab === "list_overage" ? (
          <ListOveragePlannerPanel
            groupId={groupId}
            groupName={groupName}
            onSaved={onSaved}
            onClose={onClose}
          />
        ) : tab === "import" ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Phone Numbers (one per line or comma-separated, 10-digit US)
              </label>
              <textarea
                rows={5}
                value={phonesText}
                onChange={(e) => setPhonesText(e.target.value)}
                placeholder={"4125550101\n4125550102\n3055550199"}
                data-testid="bulk-phones-textarea"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Optional Label Prefix (e.g. "PA Outreach")
              </label>
              <input
                type="text"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="Optional label prefix"
                data-testid="bulk-name-prefix-input"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            {importMut.isError && (
              <p className="text-xs text-red-600">{(importMut.error as Error).message}</p>
            )}
            {resultMsg && <p className="text-xs text-green-700 font-medium">{resultMsg}</p>}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                disabled={!phonesText.trim() || importMut.isPending}
                onClick={() => importMut.mutate()}
                data-testid="submit-bulk-import-btn"
                className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm font-medium disabled:opacity-50"
              >
                {importMut.isPending ? "Adding…" : `Add to "${groupName}"`}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Area Code (3 digits)</label>
                <input
                  type="text"
                  maxLength={3}
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="412"
                  data-testid="carrier-area-code-input"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  data-testid="carrier-qty-input"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Numbers are ordered live from Bandwidth, assigned directly to{" "}
              <strong>{groupName}</strong>, and inherit the pool's inbound forwarding.
            </p>
            {orderMut.isError && (
              <p className="text-xs text-red-600">{(orderMut.error as Error).message}</p>
            )}
            {resultMsg && <p className="text-xs text-green-700 font-medium">{resultMsg}</p>}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                disabled={areaCode.length !== 3 || quantity < 1 || orderMut.isPending}
                onClick={() => orderMut.mutate()}
                data-testid="submit-carrier-order-btn"
                className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm font-medium disabled:opacity-50"
              >
                {orderMut.isPending
                  ? "Ordering from Carrier…"
                  : `Order ${quantity} Number(s) in ${areaCode || "NPA"}`}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PoolForwardingModal({
  group,
  onClose,
  onSaved,
}: {
  group: DidGroup;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [actionType, setActionType] = useState<"external" | "agents" | "sip">(
    (group.inbound_action_type as "external" | "agents" | "sip") || "external",
  );
  const [actionData, setActionData] = useState(group.inbound_action_data ?? "");
  const [inboundMode, setInboundMode] = useState<"direct" | "press1" | "press3">(
    (group.inbound_mode as "direct" | "press1" | "press3") || "press1",
  );

  const { data: campaigns = [] } = useQuery({
    queryKey: ["eligible-campaigns"],
    queryFn: listEligibleCampaigns,
  });

  const save = useMutation({
    mutationFn: () =>
      updateDidGroupForwarding(group.did_group_id, {
        action_type: actionType,
        action_data: actionData.trim(),
        inbound_mode: inboundMode,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
      data-testid="pool-forwarding-modal"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <h2 className="text-lg font-bold">Pool Inbound Forwarding — "{group.name}"</h2>
        <p className="text-xs text-gray-500">
          Updates the default inbound destination for this pool and applies it to all member DIDs.
        </p>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-700">Destination Type</label>
          <div className="flex gap-2">
            {(["external", "agents", "sip"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setActionType(t)}
                data-testid={`pool-fwd-type-${t}`}
                className={`px-3 py-1.5 rounded text-xs font-medium capitalize ${
                  actionType === t
                    ? "bg-brand text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {t === "external" ? "External Phone" : t === "agents" ? "Agent Queue" : "SIP URI"}
              </button>
            ))}
          </div>
        </div>

        {actionType === "external" && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Forwarding Phone Number (10-digit US)
            </label>
            <input
              type="text"
              value={actionData}
              onChange={(e) => setActionData(e.target.value)}
              placeholder="4125550199"
              data-testid="pool-fwd-external-input"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
          </div>
        )}

        {actionType === "agents" && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Agent Campaign Queue</label>
            <select
              value={actionData}
              onChange={(e) => setActionData(e.target.value)}
              data-testid="pool-fwd-agents-select"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select campaign…</option>
              {campaigns.map((c) => (
                <option key={c.campaign_id} value={String(c.campaign_id)}>
                  {c.name} ({c.agent_count} agents)
                </option>
              ))}
            </select>
          </div>
        )}

        {actionType === "sip" && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">SIP Endpoint (user@host)</label>
            <input
              type="text"
              value={actionData}
              onChange={(e) => setActionData(e.target.value)}
              placeholder="1001@sip.example.com"
              data-testid="pool-fwd-sip-input"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
          </div>
        )}

        <div>
          <label className="block text-xs text-gray-600 mb-1">Inbound Callback Mode</label>
          <select
            value={inboundMode}
            onChange={(e) => setInboundMode(e.target.value as "direct" | "press1" | "press3")}
            data-testid="pool-fwd-mode-select"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="direct">Direct Connect (Immediate Ring)</option>
            <option value="press1">Press-1 IVR Gate (Press 1 to Connect / DNC on 9)</option>
            <option value="press3">Press-3 IVR Gate (Press 3 to Connect / DNC on 7)</option>
          </select>
        </div>

        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            disabled={!actionData.trim() || save.isPending}
            onClick={() => save.mutate()}
            data-testid="save-pool-forwarding-btn"
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm font-medium disabled:opacity-50"
          >
            {save.isPending ? "Applying…" : "Apply to Pool & Members"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function GroupDetail({
  group,
  allGroups,
}: {
  group: DidGroup;
  allGroups: DidGroup[];
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [showAcquire, setShowAcquire] = useState(false);
  const [acquireInitialTab, setAcquireInitialTab] = useState<"import" | "carrier" | "list_overage">(
    "import",
  );
  const [showForwarding, setShowForwarding] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "quarantined">("all");
  const [npaFilter, setNpaFilter] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [targetPoolId, setTargetPoolId] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["did-group", group.did_group_id],
    queryFn: () => getDidGroup(group.did_group_id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["did-group"] });
    qc.invalidateQueries({ queryKey: ["did-groups"] });
    qc.invalidateQueries({ queryKey: ["dids"] });
  };

  const removeOne = useMutation({
    mutationFn: (didId: number) => removeGroupMember(group.did_group_id, didId),
    onSuccess: invalidate,
  });

  const moveOne = useMutation({
    mutationFn: ({ didId, destGroupId }: { didId: number; destGroupId: number | null }) =>
      bulkMoveDids({ did_ids: [didId], target_group_id: destGroupId }),
    onSuccess: invalidate,
  });

  const renameDidMut = useMutation({
    mutationFn: ({ didId, name }: { didId: number; name: string }) => updateDid(didId, { name }),
    onSuccess: invalidate,
  });

  const bulkMoveMut = useMutation({
    mutationFn: (destGroupId: number | null) =>
      bulkMoveDids({ did_ids: [...selectedIds], target_group_id: destGroupId }),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const bulkRemoveMut = useMutation({
    mutationFn: () => removeGroupMembersBulk(group.did_group_id, [...selectedIds]),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const bulkActMut = useMutation({
    mutationFn: (action: "quarantine" | "unquarantine" | "clear_blocks") =>
      bulkDidAction({ did_ids: [...selectedIds], action }),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const clearPoolBlocksMut = useMutation({
    mutationFn: () => clearDidGroupBlocks(group.did_group_id),
    onSuccess: invalidate,
  });

  // Track the active background release job so we can poll + show a progress bar.
  const [jobId, setJobId] = useState<number | null>(null);
  const { data: job } = useQuery({
    queryKey: ["release-job", jobId],
    queryFn: () => getReleaseJob(jobId as number),
    enabled: jobId != null,
    refetchInterval: (q) => {
      const s = (q.state.data as ReleaseJob | undefined)?.status;
      return s === "complete" || s === "failed" ? false : 1500;
    },
  });
  const jobDone = job?.status === "complete" || job?.status === "failed";
  const [lastSeenDone, setLastSeenDone] = useState<number | null>(null);
  if (jobDone && job && lastSeenDone !== job.job_id) {
    setLastSeenDone(job.job_id);
    invalidate();
  }

  const release = useMutation({
    mutationFn: () => releaseDidGroup(group.did_group_id),
    onSuccess: (r) => {
      invalidate();
      if (r.job_id) setJobId(r.job_id);
      else alert(r.message || "Nothing to release.");
    },
    onError: (e: unknown) => alert(`Release failed: ${(e as Error).message}`),
  });
  const removeQ = useMutation({
    mutationFn: () => removeQuarantinedFromGroup(group.did_group_id),
    onSuccess: (r) => {
      invalidate();
      if (r.job_id) setJobId(r.job_id);
      else alert(r.message || "No quarantined numbers to release.");
    },
    onError: (e: unknown) => alert(`Failed: ${(e as Error).message}`),
  });
  const jobRunning = jobId != null && !jobDone;

  const members = data?.members ?? [];

  // Area code (NPA) breakdown
  const npaCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of members) {
      const digits = m.phone.replace(/\D/g, "");
      const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
      const npa = ten.slice(0, 3);
      if (npa.length === 3) {
        map.set(npa, (map.get(npa) ?? 0) + 1);
      }
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [members]);

  const effectiveNpaFilter = useMemo(
    () => (npaFilter && npaCounts.some(([npa]) => npa === npaFilter) ? npaFilter : null),
    [npaFilter, npaCounts],
  );

  useEffect(() => {
    setSelectedIds(new Set());
    setNpaFilter(null);
    setMemberSearch("");
    setStatusFilter("all");
  }, [group.did_group_id]);

  useEffect(() => {
    if (npaFilter && !npaCounts.some(([npa]) => npa === npaFilter)) {
      setNpaFilter(null);
    }
  }, [npaFilter, npaCounts]);

  // Filtered members
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (statusFilter === "active" && (m.is_quarantined || m.is_blocked || m.is_retired))
        return false;
      if (statusFilter === "quarantined" && !m.is_quarantined && !m.is_blocked) return false;
      if (effectiveNpaFilter) {
        const digits = m.phone.replace(/\D/g, "");
        const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
        if (!ten.startsWith(effectiveNpaFilter)) return false;
      }
      if (memberSearch.trim()) {
        const q = memberSearch.trim().toLowerCase();
        if (!m.phone.toLowerCase().includes(q) && !(m.name || "").toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [members, statusFilter, effectiveNpaFilter, memberSearch]);

  const activeCount = useMemo(
    () => members.filter((m) => !m.is_quarantined && !m.is_blocked && !m.is_retired).length,
    [members],
  );
  const flaggedCount = useMemo(
    () => members.filter((m) => m.is_quarantined || m.is_blocked).length,
    [members],
  );

  const allVisibleSelected =
    filteredMembers.length > 0 && filteredMembers.every((m) => selectedIds.has(m.did_id));

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredMembers.forEach((m) => next.delete(m.did_id));
      } else {
        filteredMembers.forEach((m) => next.add(m.did_id));
      }
      return next;
    });
  };

  const toggleOne = (didId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(didId) ? next.delete(didId) : next.add(didId);
      return next;
    });
  };

  function onRemoveQuarantined() {
    const typed = window.prompt(
      `⚠️ RELEASE QUARANTINED in "${group.name}" — this DISCONNECTS every quarantined ` +
        `number in this group at the carrier (Bandwidth) and permanently removes them from ` +
        `your account. This CANNOT be undone.\n\nType RELEASE to confirm:`,
    );
    if (typed === "RELEASE") removeQ.mutate();
  }

  function onRelease() {
    const n = members.length;
    if (!n) {
      alert("This group has no numbers to release.");
      return;
    }
    const typed = window.prompt(
      `⚠️ RELEASE "${group.name}" — this DISCONNECTS all ${n} number(s) at the carrier ` +
        `(Bandwidth) and permanently removes them from your account. This CANNOT be undone — ` +
        `the numbers are gone.\n\nType RELEASE to confirm:`,
    );
    if (typed === "RELEASE") release.mutate();
  }

  const otherGroups = allGroups.filter((g) => g.did_group_id !== group.did_group_id);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden space-y-0" data-testid="group-detail-panel">
      {/* Pool Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
        <div>
          <h2 className="font-semibold text-lg" data-testid="selected-group-title">
            {group.name}{" "}
            <span className="text-gray-400 text-sm font-normal">
              — {members.length} number{members.length === 1 ? "" : "s"}
            </span>
          </h2>
          <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
            <span data-testid="pool-forwarding-summary">
              {formatForwarding(
                data?.inbound_action_type ?? group.inbound_action_type,
                data?.inbound_action_data ?? group.inbound_action_data,
                data?.inbound_mode ?? group.inbound_mode,
              )}
            </span>
            <button
              type="button"
              onClick={() => setShowForwarding(true)}
              data-testid="open-pool-forwarding-btn"
              className="text-brand hover:underline font-medium"
            >
              Configure Forwarding
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setAcquireInitialTab("list_overage");
              setShowAcquire(true);
            }}
            data-testid="open-list-overage-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded px-3 py-1.5 text-xs font-medium"
          >
            Buy Overage by List
          </button>
          <button
            type="button"
            onClick={() => {
              setAcquireInitialTab("import");
              setShowAcquire(true);
            }}
            data-testid="open-acquire-into-pool-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 text-xs font-medium"
          >
            + New / Order Numbers
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="open-add-members-btn"
            className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1.5 text-xs font-medium"
          >
            + Move Existing Numbers In
          </button>
          {flaggedCount > 0 && (
            <button
              type="button"
              onClick={() => clearPoolBlocksMut.mutate()}
              disabled={clearPoolBlocksMut.isPending}
              data-testid="clear-pool-blocks-btn"
              className="border border-amber-300 text-amber-800 hover:bg-amber-50 rounded px-3 py-1.5 text-xs"
            >
              {clearPoolBlocksMut.isPending ? "Clearing…" : `Clear Blocks (${flaggedCount})`}
            </button>
          )}
          <button
            type="button"
            onClick={onRemoveQuarantined}
            disabled={removeQ.isPending || jobRunning}
            className="border border-red-300 text-red-700 hover:bg-red-50 rounded px-2.5 py-1.5 text-xs disabled:opacity-50"
          >
            {removeQ.isPending ? "Starting…" : "Release Quarantined"}
          </button>
          <button
            type="button"
            onClick={onRelease}
            disabled={release.isPending || jobRunning}
            className="border border-red-300 text-red-700 hover:bg-red-50 rounded px-2.5 py-1.5 text-xs disabled:opacity-50"
          >
            {release.isPending ? "Starting…" : "Release Pool"}
          </button>
        </div>
      </div>

      {/* Pool KPI & Area Code Distribution Strip */}
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs">
          <span className="px-2.5 py-1 rounded bg-white border border-gray-200 font-medium">
            Total: <strong>{members.length}</strong>
          </span>
          <span className="px-2.5 py-1 rounded bg-green-50 border border-green-200 text-green-800 font-medium">
            Active: <strong>{activeCount}</strong>
          </span>
          <span className="px-2.5 py-1 rounded bg-red-50 border border-red-200 text-red-800 font-medium">
            Quarantined / Blocked: <strong>{flaggedCount}</strong>
          </span>
        </div>

        {npaCounts.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap text-xs">
            <span className="text-gray-400 mr-1">Area Codes:</span>
            <button
              type="button"
              onClick={() => setNpaFilter(null)}
              data-testid="npa-pill-all"
              className={`px-2 py-0.5 rounded ${
                effectiveNpaFilter === null
                  ? "bg-gray-800 text-white"
                  : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
              }`}
            >
              All
            </button>
            {npaCounts.slice(0, 10).map(([npa, cnt]) => (
              <button
                key={npa}
                type="button"
                onClick={() => setNpaFilter(effectiveNpaFilter === npa ? null : npa)}
                data-testid={`npa-pill-${npa}`}
                className={`px-2 py-0.5 rounded font-mono ${
                  effectiveNpaFilter === npa
                    ? "bg-brand text-white"
                    : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                {npa} ({cnt})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filter & Search Bar inside Pool */}
      <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={memberSearch}
          onChange={(e) => setMemberSearch(e.target.value)}
          placeholder="Filter pool numbers by phone, area code, or label…"
          data-testid="pool-member-search"
          className="flex-1 min-w-[200px] border border-gray-300 rounded px-3 py-1.5 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "quarantined")}
          data-testid="pool-status-filter"
          className="border border-gray-300 rounded px-2.5 py-1.5 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active Only</option>
          <option value="quarantined">Quarantined / Blocked</option>
        </select>
      </div>

      {/* Multi-Select Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div
          className="px-4 py-2.5 bg-blue-50 border-b border-blue-200 flex flex-wrap items-center justify-between gap-2 text-xs"
          data-testid="pool-bulk-action-bar"
        >
          <span className="font-semibold text-blue-900">
            {selectedIds.size} number{selectedIds.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={targetPoolId}
              onChange={(e) => setTargetPoolId(e.target.value)}
              data-testid="bulk-move-target-select"
              className="border border-blue-300 rounded px-2.5 py-1 text-xs bg-white"
            >
              <option value="">Move selected to pool…</option>
              {otherGroups.map((g) => (
                <option key={g.did_group_id} value={String(g.did_group_id)}>
                  {g.name} ({g.member_count})
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!targetPoolId || bulkMoveMut.isPending}
              onClick={() => bulkMoveMut.mutate(Number(targetPoolId))}
              data-testid="bulk-move-confirm-btn"
              className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1 font-medium disabled:opacity-50"
            >
              {bulkMoveMut.isPending ? "Moving…" : "Move to Pool"}
            </button>
            <button
              type="button"
              disabled={bulkRemoveMut.isPending}
              onClick={() => bulkRemoveMut.mutate()}
              data-testid="bulk-unassign-btn"
              className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 rounded px-3 py-1"
            >
              {bulkRemoveMut.isPending ? "Removing…" : "Unassign from Pool"}
            </button>
            <button
              type="button"
              disabled={bulkActMut.isPending}
              onClick={() => bulkActMut.mutate("quarantine")}
              className="bg-white border border-amber-300 hover:bg-amber-50 text-amber-800 rounded px-2.5 py-1"
            >
              Quarantine
            </button>
            <button
              type="button"
              disabled={bulkActMut.isPending}
              onClick={() => bulkActMut.mutate("clear_blocks")}
              className="bg-white border border-green-300 hover:bg-green-50 text-green-800 rounded px-2.5 py-1"
            >
              Clear Blocks
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-gray-500 hover:underline ml-1"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {job && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="font-medium">
              {job.status === "complete"
                ? `Release complete — ${job.released} disconnected${
                    job.failed ? `, ${job.failed} failed (will retry)` : ""
                  }`
                : job.status === "failed"
                ? `Release stopped — ${job.released} disconnected, ${job.failed} not (will retry)`
                : `Releasing ${job.quarantined_only ? "quarantined " : ""}numbers at the carrier…`}
            </span>
            <span className="text-gray-500 tabular-nums">
              {job.done}/{job.total}
            </span>
          </div>
          <div className="h-2 rounded bg-gray-200 overflow-hidden">
            <div
              className={`h-full transition-all ${
                job.status === "failed"
                  ? "bg-red-500"
                  : job.status === "complete"
                  ? "bg-green-500"
                  : "bg-brand"
              }`}
              style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }}
            />
          </div>
          {jobDone && (
            <button
              onClick={() => {
                setJobId(null);
              }}
              className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500 text-sm p-4">Loading…</p>
      ) : (
        <table className="w-full text-sm" data-testid="pool-members-table">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  data-testid="select-all-pool-members"
                />
              </th>
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Inbound Forwarding</th>
              <th className="px-3 py-2">Reputation</th>
              <th className="px-3 py-2">Move to Pool</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  {members.length === 0
                    ? "No numbers in this pool yet. Click '+ New / Order Numbers' or '+ Move Existing Numbers In' above."
                    : "No numbers match the current filter."}
                </td>
              </tr>
            ) : (
              filteredMembers.map((d) => (
                <tr key={d.did_id} className="hover:bg-gray-50/70" data-testid={`pool-member-row-${d.phone}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(d.did_id)}
                      onChange={() => toggleOne(d.did_id)}
                      data-testid={`pool-member-checkbox-${d.phone}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono font-medium">{d.phone}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => {
                        const next = window.prompt("Edit number label:", d.name || "");
                        if (next !== null) renameDidMut.mutate({ didId: d.did_id, name: next.trim() });
                      }}
                      title="Click to edit label"
                      className="text-left hover:underline text-gray-700"
                    >
                      {d.name || "—"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {formatForwarding(d.action_type, d.action_data, d.inbound_mode)}
                  </td>
                  <td className="px-3 py-2">{statusBadge(d)}</td>
                  <td className="px-3 py-2">
                    <select
                      value={String(group.did_group_id)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "unassign") {
                          moveOne.mutate({ didId: d.did_id, destGroupId: null });
                        } else if (Number(val) !== group.did_group_id) {
                          moveOne.mutate({ didId: d.did_id, destGroupId: Number(val) });
                        }
                      }}
                      data-testid={`row-move-select-${d.phone}`}
                      className="border border-gray-200 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value={String(group.did_group_id)}>In: {group.name}</option>
                      <option value="unassign">-- Unassign from Pool --</option>
                      {otherGroups.map((g) => (
                        <option key={g.did_group_id} value={String(g.did_group_id)}>
                          Move → {g.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => removeOne.mutate(d.did_id)}
                      data-testid={`remove-member-btn-${d.phone}`}
                      className="text-red-600 hover:underline text-xs"
                    >
                      Unassign
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddMembersModal
          groupId={group.did_group_id}
          groupName={group.name}
          allGroups={allGroups}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            invalidate();
          }}
        />
      )}
      {showAcquire && (
        <AcquireIntoPoolModal
          groupId={group.did_group_id}
          groupName={group.name}
          initialTab={acquireInitialTab}
          onClose={() => setShowAcquire(false)}
          onSaved={() => {
            invalidate();
          }}
        />
      )}
      {showForwarding && (
        <PoolForwardingModal
          group={group}
          onClose={() => setShowForwarding(false)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}

export function CallerIdGroupsPage() {
  const qc = useQueryClient();
  const {
    data: groups,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["did-groups"],
    queryFn: listDidGroups,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");
  const [groupFilter, setGroupFilter] = useState("");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["did-groups"] });
    qc.invalidateQueries({ queryKey: ["dids"] });
  };
  const create = useMutation({
    mutationFn: (name: string) => createDidGroup(name),
    onSuccess: (createdGroupId) => {
      setNewName("");
      setSelectedId(createdGroupId);
      invalidate();
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => updateDidGroup(id, name),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async ({ group }: { group: DidGroup }) => {
      if (group.member_count > 0) {
        const detail = await getDidGroup(group.did_group_id);
        const ids = detail.members.map((m) => m.did_id);
        if (ids.length > 0) {
          await removeGroupMembersBulk(group.did_group_id, ids);
        }
      }
      await deleteDidGroup(group.did_group_id);
    },
    onSuccess: () => {
      setSelectedId(null);
      invalidate();
    },
  });

  const allGroups = groups ?? [];
  const filteredGroups = useMemo(() => {
    if (!groupFilter.trim()) return allGroups;
    const q = groupFilter.trim().toLowerCase();
    return allGroups.filter((g) => g.name.toLowerCase().includes(q));
  }, [allGroups, groupFilter]);

  const selected =
    allGroups.find((g) => g.did_group_id === selectedId) ?? allGroups[0] ?? null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">Caller ID Pools (DID Groups)</h1>
          <p className="text-sm text-gray-500">
            Create pools, order or import numbers directly into pools, configure pool-wide callback
            forwarding, and move numbers between pools in bulk.
          </p>
        </div>
        <Link
          to="/dids"
          className="px-3 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
        >
          View All Phone Numbers →
        </Link>
      </div>

      {isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Groups list */}
          <div className="bg-white rounded-lg shadow overflow-hidden lg:col-span-1 h-fit">
            <div className="p-3 border-b border-gray-100 space-y-2">
              <div className="flex gap-2">
                <input
                  className="flex-1 border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                  placeholder="New pool name (e.g. East Coast Pool)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="new-pool-name-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newName.trim()) create.mutate(newName.trim());
                  }}
                />
                <button
                  disabled={!newName.trim() || create.isPending}
                  onClick={() => create.mutate(newName.trim())}
                  data-testid="create-pool-btn"
                  className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {create.isPending ? "Creating…" : "Create Pool"}
                </button>
              </div>
              {allGroups.length > 5 && (
                <input
                  className="w-full border border-gray-200 rounded px-2.5 py-1 text-xs"
                  placeholder="Filter pools…"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                />
              )}
            </div>
            {isLoading ? (
              <p className="text-gray-500 text-sm p-4">Loading…</p>
            ) : filteredGroups.length === 0 ? (
              <p className="text-gray-500 text-sm p-4">No pools yet. Create your first pool above.</p>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[70vh] overflow-auto" data-testid="pool-list">
                {filteredGroups.map((g) => {
                  const isSel = selected?.did_group_id === g.did_group_id;
                  return (
                    <li
                      key={g.did_group_id}
                      data-testid={`pool-item-${g.name}`}
                      className={`px-4 py-2.5 flex items-center justify-between cursor-pointer ${
                        isSel ? "bg-brand/10 border-l-4 border-brand" : "hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedId(g.did_group_id)}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="text-sm font-medium truncate">
                          {g.name}{" "}
                          <span className="text-gray-400 font-normal">({g.member_count})</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                          {typeof g.active_count === "number" && (
                            <span className="text-green-700">{g.active_count} active</span>
                          )}
                          {(g.quarantined_count ?? 0) > 0 && (
                            <span className="text-red-600">{g.quarantined_count} quarantined</span>
                          )}
                        </div>
                      </div>
                      <span className="space-x-2 text-xs whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const name = prompt("Rename pool", g.name);
                            if (name && name.trim())
                              rename.mutate({ id: g.did_group_id, name: name.trim() });
                          }}
                          data-testid={`rename-pool-btn-${g.name}`}
                          className="text-brand hover:underline"
                        >
                          Rename
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const msg =
                              g.member_count > 0
                                ? `Pool "${g.name}" has ${g.member_count} number(s). Unassign its numbers and delete the pool?`
                                : `Delete pool "${g.name}"?`;
                            if (confirm(msg)) remove.mutate({ group: g });
                          }}
                          data-testid={`delete-pool-btn-${g.name}`}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Selected group detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <GroupDetail group={selected} allGroups={allGroups} />
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 text-sm">
                Select or create a pool to manage its numbers.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
